<template>
  <div class="organization-card">
    <div
      v-if="donorStore.isLoading"
      class="loading-overlay"
    ></div>
    <div class="organization-image">
      <img
        :src="`https://ddb9l06w3jzip.cloudfront.net/uploadedFiles/giving_affiliate/organizations/logo/${favorite.organizations_id}'`"
        alt="Hero Image"
      />
      <FavoriteHeart
        class="absolute fh"
        :favorite="favorite"
        @unFavorite="showDeleteDialog = true"
      />
    </div>
    <div class="bottom-organization-card">
      <div
        class="organization-name"
        data-test="organization-name"
      >
        {{ favorite.organization.organizationName }}
      </div>
      <Button
        :style="`width: 100%; justify-content: center; color: ${getContrastYIQ(donorStore.mainColor)}`"
        @click="goToDetailPage(favorite.organization)"
        data-test="view-organization-button"
      >
        View Organization
      </Button>
    </div>
  </div>
  <Modal
    v-if="showDeleteDialog"
    :show="showDeleteDialog"
    justifyContent="flex-start"
    textAlign="flex-start"
    width="580px"
    height="373px"
    :bodyFullHeight="true"
    @close="showDeleteDialog = false"
    data-test="delete-dialog"
  >
    <template #header>Remove</template>
    <div class="flex w-full">
      <div class="removeFavoriteTextContainer">
        <p>Are you sure you’d like to remove {{ favorite.organization.organizationName }} from your favorite causes?</p>
      </div>
    </div>

    <template #footer>
      <Button
        variant="link"
        label="Cancel"
        @click="showDeleteDialog = false"
        data-test="cancel-button"
      >
      </Button>
      <Button
        label="Remove"
        class="removeFavoriteButton"
        :style="{ width: '138px' }"
        custom-font="false"
        @click="deleteFavorite()"
        :loading="deleteFavoriteLoading"
        data-test="remove-button"
      >
      </Button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import filters from '@/helpers/filters';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import useIsMobile from '@/composables/useIsMobile.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFavoriteStore } from '@/stores/FavoriteStore.js';
import { useRouter } from 'vue-router';
import PencilBlack from '@/assets/PencilBlack.svg?raw';
import Eye from '@/assets/Eye.svg?raw';
import Deactivate from '@/assets/Deactivate.svg?raw';
import FavoriteHeart from '@/components/favorites/FavoriteHeart.vue';
import Modal from '@/components/Modal.vue';

const showDeleteDialog = ref(false);
const { isMobile } = useIsMobile();

const donorStore = useDonorStore();
const favoritesStore = useFavoriteStore();
const router = useRouter();

const props = defineProps({
  favorite: Object,
  showDetailsButton: {
    type: Boolean,
    default: true,
  },
});

// view organization page
const openOrganizationPage = () => {
  window.open(props.organization.domain.replace(/\/$/, '') + '/organization/' + props.organization.urlLink, '_blank');
};

// Used to set the status badge's color
const statusToTypeMap = {
  Created: 'pending',
  Submitted: 'pending',
  Declined: 'error',
  Deactivated: 'canceled',
  Published: 'success',
  Expired: 'canceled',
};

const baseMenuItems = [
  {
    text: 'Visit Page',
    emit: 'visitPage',
    disabled: false,
    disabled_text: null,
    type: 'default',
    icon: Eye,
  },

  {
    text: 'Edit Page',
    emit: 'editPage',
    disabled: false,
    disabled_text: null,
    type: 'default',
    icon: PencilBlack,
  },
];

const goToDetailPage = (organization) => {
  // Open in new page
  window.open(`${donorStore.domain.replace(/\/$/, '')}/organization/${organization.urlLink}`, '_blank');
};

const amount = ref(0);
const goal = ref(0);

const getAmount = (organization, raw = false) => {
  if (organization.status == 'published') {
    const data = donorStore.organizationDonationData[organization.organizations_id];
    if (!data) {
      return raw ? 0 : filters.formatMoney(0, 0);
    }
    amount.value = data.amount;
    return raw ? data.amount : filters.formatMoney(data.amount, 0);
  } else {
    amount.value = organization.amountRaised_cached;
    return filters.formatMoney(organization.amountRaised_cached, 0);
  }
};

const getGoal = (organization, cachedGoal, raw = false) => {
  if (organization.status == 'published') {
    const data = donorStore.organizationDonationData[organization.organizations_id];
    if (!data) {
      return raw ? cachedGoal : filters.formatMoney(cachedGoal, 0);
    }
    goal.value = data.goal;
    return raw ? data.goal : filters.formatMoney(data.goal, 0);
  } else {
    goal.value = organization.goal;
    return filters.formatMoney(organization.goal, 0);
  }
};

// Method to calculate progress bar width
const calculateWidth = () => {
  const percentComplete = Math.min(amount.value / goal.value, 1); // Ensure it does not exceed 100%
  return `${percentComplete * 100}%`;
};

const deleteFavoriteLoading = ref(false);
const deleteFavorite = async () => {
  deleteFavoriteLoading.value = true;
  await favoritesStore.removeOrgFromFavorites(props.favorite.donorAccounts_favorites_id);
  deleteFavoriteLoading.value = false;
  showDeleteDialog.value = false;
};
</script>

<style scoped>
.fh {
  right: 5px;
  top: 5px;
}
.organization-card {
  fill: var(--White, #fff);
  filter: drop-shadow(0px 3px 20px rgba(0, 0, 0, 0.07));
  width: 288px;
  min-height: 288px;
  background-color: var(--White, #fff);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  align-items: center;
  min-width: 250px;
  max-width: 300px;
}
.organization-card.is-loading {
  position: relative;
}
.bottom-organization-card {
  width: 100%;
  padding: 8px 16px 16px 16px;
  text-align: center;
}
.organization-name {
  font-weight: bold;
  height: 60px;
  display: -webkit-box;
  overflow: hidden;
  max-width: 100%;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  height: 50px; /* Adjust height to fit content */
}
.organization-image {
  position: relative;
  width: 100%;
  height: 162px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px 8px 0 0; /* Apply rounded corners to top-left and top-right */
}
.organization-image img {
  width: 100%; /* Ensure image takes full width */
  height: 100%; /* Ensure image takes full height */
  object-fit: contain; /* Cover the container without being clipped */
}
.removeFavoriteTextContainer {
  max-width: 470px;
  font-weight: 500;
  font-size: 1.625rem;
}
.removeFavoriteTextContainer .removeFavoriteTextHeader {
  font-size: 1.25rem;
}
@media screen and (max-width: 768px) {
  .removeFavoriteTextContainer {
    font-size: 1.25rem;
  }
}
</style>
